import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RichTextEditor } from '../../components/RichTextEditor';
import { PageHeader } from '../../components/PageHeader';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { SkeletonTable } from '../../components/Skeleton';
import {
  useCreateSnippet,
  useDeleteSnippet,
  usePlaceholders,
  useSnippets,
  useUpdateSnippet,
} from './queries';

/**
 * Verwaltung der Textbausteine -- F1-36, K-18.
 *
 * Die Liste der Platzhalter steht neben dem Eingabefeld und nicht in einer
 * Hilfeseite: Wer einen Baustein schreibt, braucht sie in genau diesem
 * Moment. Ein unbekannter Platzhalter wird vom Server schon beim Speichern
 * abgelehnt -- nicht erst, wenn er in einer Kundenantwort steht.
 */
export function SnippetsPage() {
  const { t } = useTranslation();
  const snippets = useSnippets();
  const placeholders = usePlaceholders();
  const create = useCreateSnippet();
  const update = useUpdateSnippet();
  const remove = useDeleteSnippet();

  const [shortcut, setShortcut] = useState('');
  const [name, setName] = useState('');
  const [body, setBody] = useState('');

  return (
    <>
      <PageHeader title={t('snippets.title')} subtitle={t('snippets.subtitle')} />

      <div className="detail-grid">
        <div className="stack">
          <section className="card">
            <h2>{t('snippets.new')}</h2>

            <div className="field">
              <label className="field__label" htmlFor="snippet-shortcut">
                {t('snippets.shortcut')}
              </label>
              <input
                id="snippet-shortcut"
                className="field__control"
                value={shortcut}
                placeholder="gruss"
                onChange={(event) => {
                  setShortcut(event.target.value);
                }}
              />
              <span className="field__hint">{t('snippets.shortcutHint')}</span>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="snippet-name">
                {t('masterdata.name')}
              </label>
              <input
                id="snippet-name"
                className="field__control"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                }}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="snippet-body">
                {t('snippets.body')}
              </label>
              {/* Ohne Bild: Ein Baustein wird in beliebige Tickets
                  eingesetzt, und ein eingebettetes Bild zeigte auf einen
                  Anhang, den es dort nicht gibt. */}
              <RichTextEditor
                id="snippet-body"
                value={body}
                onChange={setBody}
                ariaLabel={t('snippets.body')}
                features={{ image: false }}
                minRows={8}
              />
            </div>

            {create.isError ? <ErrorNotice error={create.error} /> : null}

            <Button
              variant="primary"
              disabled={!shortcut.trim() || !name.trim() || !body.trim()}
              busy={create.isPending}
              onClick={() => {
                create.mutate(
                  { shortcut: shortcut.trim(), name: name.trim(), body, is_active: true },
                  {
                    onSuccess: () => {
                      setShortcut('');
                      setName('');
                      setBody('');
                    },
                  },
                );
              }}
            >
              {t('masterdata.add')}
            </Button>
          </section>

          <section className="card card--flush">
            {snippets.isPending ? <SkeletonTable columns={4} /> : null}
            {snippets.isError ? <ErrorNotice error={snippets.error} /> : null}

            {snippets.data?.length === 0 ? (
              <EmptyState
                icon="snippets"
                title={t('snippets.empty')}
                hint={t('snippets.emptyHint')}
              />
            ) : null}

            {snippets.data && snippets.data.length > 0 ? (
              <div className="table-wrapper">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">{t('snippets.shortcut')}</th>
                      <th scope="col">{t('masterdata.name')}</th>
                      <th scope="col">{t('masterdata.isActive')}</th>
                      <th scope="col">{t('tickets.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {snippets.data.map((snippet) => (
                      <tr key={snippet.id}>
                        <td className="table__number">@@{snippet.shortcut}</td>
                        <td>{snippet.name}</td>
                        <td>
                          <Badge tone={snippet.is_active ? 'success' : 'neutral'}>
                            {snippet.is_active ? t('masterdata.yes') : t('masterdata.no')}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            variant="ghost"
                            onClick={() => {
                              update.mutate({
                                snippetId: snippet.id,
                                payload: { is_active: !snippet.is_active },
                              });
                            }}
                          >
                            {snippet.is_active ? t('snippets.deactivate') : t('snippets.activate')}
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => {
                              remove.mutate(snippet.id);
                            }}
                          >
                            {t('masterdata.delete')}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            {update.isError ? <ErrorNotice error={update.error} /> : null}
            {remove.isError ? <ErrorNotice error={remove.error} /> : null}
          </section>
        </div>

        <aside className="card">
          <h2>{t('snippets.placeholders')}</h2>
          <p className="table__muted">{t('snippets.placeholdersHint')}</p>
          <dl className="definition-list">
            {(placeholders.data ?? []).map((placeholder) => (
              <div key={placeholder.key} className="history__change">
                <dt className="table__number">{`{{${placeholder.key}}}`}</dt>
                <dd>{placeholder.description}</dd>
              </div>
            ))}
          </dl>
        </aside>
      </div>
    </>
  );
}
